import { CSSProperties, useEffect, useState } from 'react';

import { lottieAnimations } from '@backrs_ui/common-ui/src/assets/lottieAnimations';
import { RADIUS } from '@backrs_ui/common-ui/src/designSystem/spacing/spacing';

import { Box, SxProps, Theme } from '@mui/material';
import Lottie from 'lottie-react';

type BadgeCircleSize = 'sm' | 'md' | 'lg';

type SizeConfig = Readonly<{
  diameter: string;
  noBorderInnerCircleMargin: number;
  withBorderInnerCircleMargin: number;
}>;

type BadgeCircleProps = Readonly<{
  loading?: boolean;
  showOutline?: boolean;
  size?: BadgeCircleSize;
  innerOnly?: boolean;
  autoStopMs?: number;
  crossedOut?: boolean;
  gray?: boolean;
  staticDotDiameter?: string;
}>;

const SIZE_CONFIG: Record<BadgeCircleSize, SizeConfig> = {
  sm: {
    diameter: '12px',
    noBorderInnerCircleMargin: 4,
    withBorderInnerCircleMargin: 1,
  },
  md: {
    diameter: '16px',
    noBorderInnerCircleMargin: 4,
    withBorderInnerCircleMargin: 1,
  },
  lg: {
    diameter: '20px',
    noBorderInnerCircleMargin: 6,
    withBorderInnerCircleMargin: 1,
  },
};

export const BadgeCircle = ({
  loading = false,
  showOutline = false,
  size = 'sm',
  innerOnly = false,
  autoStopMs,
  crossedOut = false,
  gray = false,
  staticDotDiameter,
}: BadgeCircleProps) => {
  const [animating, setAnimating] = useState(loading);
  const useGray = gray || crossedOut;

  useEffect(() => {
    setAnimating(loading);

    if (!loading || !autoStopMs) return;

    const timer = setTimeout(() => setAnimating(false), autoStopMs);

    return () => clearTimeout(timer);
  }, [loading, autoStopMs]);

  return (
    <Box sx={iconWrapperSx(size)}>
      {animating ? (
        <Lottie
          animationData={lottieAnimations.pulsingCircle}
          loop={true}
          style={lottieStyle(size)}
        />
      ) : (
        <Box sx={crossedOut ? crossedOutWrapperSx(size) : undefined}>
          <Box sx={outerRingSx(showOutline, size, useGray)}>
            {innerOnly ? (
              <Box sx={innerOnlyCircleSx(size, useGray, staticDotDiameter)}></Box>
            ) : (
              <Box sx={middleRingSx(useGray)}>
                <Box sx={innerCircleSx(showOutline, size, useGray)}></Box>
              </Box>
            )}
          </Box>
          {crossedOut && <Box sx={strikethroughSx(size)} />}
        </Box>
      )}
    </Box>
  );
};

// Outermost ring with optional black border
const outerRingSx =
  (showOutline: boolean, size: BadgeCircleSize, gray = false): SxProps<Theme> =>
  ({ backrs }) => ({
    borderRadius: RADIUS['radius-full'],
    width: SIZE_CONFIG[size].diameter,
    height: SIZE_CONFIG[size].diameter,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: showOutline
      ? `1px solid ${gray ? backrs.colors['Gray/400'] : backrs.colors['Colors/Base/Black']}`
      : 'none',
  });

const middleRingSx =
  (gray = false): SxProps<Theme> =>
  ({ backrs }) => ({
    borderRadius: RADIUS['radius-full'],
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: gray ? backrs.colors['Gray/200'] : backrs.colors['Blue/200'],
    padding: '2px',
  });

const innerCircleSx =
  (showOutline: boolean, size: BadgeCircleSize, gray = false): SxProps<Theme> =>
  ({ backrs }) => {
    const noBorderMargin = SIZE_CONFIG[size].noBorderInnerCircleMargin;
    const withBorderMargin = SIZE_CONFIG[size].withBorderInnerCircleMargin;

    return {
      borderRadius: RADIUS['radius-full'],
      backgroundColor: gray ? backrs.colors['Gray/400'] : backrs.colors['Brand/500'],

      ...(showOutline && {
        width: `calc(100% - ${withBorderMargin * 2}px)`,
        height: `calc(100% - ${withBorderMargin * 2}px)`,
        border: `1px solid ${gray ? backrs.colors['Gray/400'] : backrs.colors['Colors/Base/Black']}`,
        margin: `${withBorderMargin}px`,
      }),

      ...(!showOutline && {
        width: `calc(100% - ${noBorderMargin}px)`,
        height: `calc(100% - ${noBorderMargin}px)`,
        margin: `${noBorderMargin / 2}px`,
        border: 'none',
      }),
    };
  };

const innerOnlyCircleSx =
  (size: BadgeCircleSize, gray = false, diameterOverride?: string): SxProps<Theme> =>
  ({ backrs }) => {
    const margin = SIZE_CONFIG[size].noBorderInnerCircleMargin;

    return {
      borderRadius: RADIUS['radius-full'],
      backgroundColor: gray ? backrs.colors['Gray/400'] : backrs.colors['Brand/500'],
      width: diameterOverride ?? `calc(100% - ${margin}px)`,
      height: diameterOverride ?? `calc(100% - ${margin}px)`,
    };
  };

const crossedOutWrapperSx = (size: BadgeCircleSize): SxProps<Theme> => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: SIZE_CONFIG[size].diameter,
  height: SIZE_CONFIG[size].diameter,
});

const strikethroughSx =
  (size: BadgeCircleSize): SxProps<Theme> =>
  ({ backrs }) => {
    const d = parseInt(SIZE_CONFIG[size].diameter);
    const lineLength = d;

    return {
      position: 'absolute',
      top: '50%',
      left: '50%',
      width: `${lineLength}px`,
      height: '1.5px',
      backgroundColor: backrs.colors['Colors/Base/Black'],
      transform: 'translate(-50%, -50%) rotate(-45deg)',
      pointerEvents: 'none',
    };
  };

const lottieStyle = (size: BadgeCircleSize): CSSProperties => ({
  width: SIZE_CONFIG[size].diameter,
  height: SIZE_CONFIG[size].diameter,
  display: 'flex',
  lineHeight: 0,
});

const iconWrapperSx = (size: BadgeCircleSize): SxProps<Theme> => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: SIZE_CONFIG[size].diameter,
  height: SIZE_CONFIG[size].diameter,
});
